import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ForwardedRef,
  forwardRef,
  ReactNode,
} from 'react';

/**
 * Variants and sizes are declared as runtime arrays, not bare union types,
 * so something can enumerate them.
 *
 * The component composes its class names — `kk-button--${variant}`,
 * `kk-button--${size}` — which means a variant whose rule is missing from
 * `_button.scss` does not fail to compile and does not throw. It renders
 * with the base `.kk-button` recipe and nothing else, and the only way to
 * notice is to look at it. That has already happened once on this codebase:
 * the collection timeslot modal's submit button rendered unstyled for weeks
 * after the primitive took over the CTA recipe.
 *
 * A union type is erased before any test can read it, so the CSS contract
 * was unenforceable while these lists lived only in the type system.
 * `Button.contract.test.ts` iterates these arrays against the stylesheet.
 *
 * `as const` + indexed access keeps the exported types byte-identical to the
 * unions they replace, so no call site changes.
 */
export const BUTTON_VARIANTS = [
  'primary',
  // Filled white with brand text — for CTAs on a brand-coloured surface
  // (the site header bar) where `primary` would vanish into its background.
  'inverse',
  'secondary',
  'tertiary',
  'danger',
  'success',
  'link',
] as const;

export const BUTTON_SIZES = ['sm', 'md', 'lg'] as const;

/**
 * Class-name modifiers the component can emit that are not variants or
 * sizes. Kept beside them so the contract test covers every branch of
 * `classes()` rather than only the two it was written for.
 */
export const BUTTON_STATE_MODIFIERS = [
  'icon-only',
  'full-width',
  'loading',
] as const;

export type ButtonVariant = typeof BUTTON_VARIANTS[number];

export type ButtonSize = typeof BUTTON_SIZES[number];

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  iconOnly?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
  className?: string;
  'aria-label'?: string;
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'data-testid'?: string;
}

export interface ButtonAsButtonProps
  extends CommonProps,
    Omit<
      ButtonHTMLAttributes<HTMLButtonElement>,
      keyof CommonProps | 'aria-label' | 'aria-controls' | 'aria-expanded'
    > {
  as?: 'button';
  type?: 'button' | 'submit' | 'reset';
  href?: never;
}

export interface ButtonAsAnchorProps
  extends CommonProps,
    Omit<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      keyof CommonProps | 'aria-label' | 'aria-controls' | 'aria-expanded'
    > {
  as: 'a';
  href: string;
  type?: never;
}

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

function classes(
  variant: ButtonVariant,
  size: ButtonSize,
  iconOnly: boolean,
  fullWidth: boolean,
  loading: boolean,
  className?: string,
): string {
  return [
    'kk-button',
    `kk-button--${variant}`,
    `kk-button--${size}`,
    iconOnly ? 'kk-button--icon-only' : null,
    fullWidth ? 'kk-button--full-width' : null,
    loading ? 'kk-button--loading' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

function Spinner(): JSX.Element {
  return <span className="kk-button__spinner" aria-hidden="true" />;
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      iconOnly = false,
      fullWidth = false,
      children,
      className,
      'aria-label': ariaLabel,
      'aria-controls': ariaControls,
      'aria-expanded': ariaExpanded,
      'data-testid': dataTestId,
      ...rest
    } = props;

    // Runtime invariant — iconOnly without aria-label is an a11y bug.
    // Warn once in development; ship the bug visible to QA rather than silent.
    if (
      process.env.NODE_ENV !== 'production' &&
      iconOnly &&
      !ariaLabel
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Button] iconOnly buttons must provide an aria-label for screen readers.',
      );
    }

    const cls = classes(variant, size, iconOnly, fullWidth, loading, className);
    const isDisabled = disabled || loading;

    // Spinner replaces leftIcon (or sits in the leftIcon slot if no icon was
    // provided) — this avoids a layout shift when toggling loading.
    const left = loading ? <Spinner /> : leftIcon;

    // iconOnly buttons accept the glyph as `children` directly (rendered raw,
    // no label span) OR via `leftIcon`. Without this, passing an SVG as children
    // to an iconOnly button silently drops the icon.
    const labelNode = children
      ? iconOnly
        ? children
        : <span className="kk-button__label">{children}</span>
      : null;

    if (props.as === 'a') {
      const { as: _as, href, ...anchorRest } = rest as Omit<
        ButtonAsAnchorProps,
        keyof CommonProps
      >;
      // When rendered as an anchor, "disabled" + "loading" are advisory; we
      // emulate via aria-disabled + tabIndex=-1 + click suppression.
      const onClickGuard = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        const orig = (anchorRest as AnchorHTMLAttributes<HTMLAnchorElement>)
          .onClick;
        if (orig) orig(e);
      };
      return (
        <a
          {...(anchorRest as AnchorHTMLAttributes<HTMLAnchorElement>)}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          href={(props as ButtonAsAnchorProps).href}
          className={cls}
          aria-label={ariaLabel}
          aria-controls={ariaControls}
          aria-expanded={ariaExpanded}
          aria-disabled={isDisabled || undefined}
          aria-busy={loading || undefined}
          tabIndex={isDisabled ? -1 : undefined}
          data-testid={dataTestId}
          onClick={onClickGuard}
        >
          {left}
          {labelNode}
          {!loading && rightIcon}
        </a>
      );
    }

    const buttonRest = rest as Omit<ButtonAsButtonProps, keyof CommonProps>;
    const buttonType =
      (buttonRest as ButtonHTMLAttributes<HTMLButtonElement>).type ?? 'button';

    const onClickGuard = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      const orig = (buttonRest as ButtonHTMLAttributes<HTMLButtonElement>)
        .onClick;
      if (orig) orig(e);
    };

    return (
      <button
        {...(buttonRest as ButtonHTMLAttributes<HTMLButtonElement>)}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        type={buttonType}
        className={cls}
        disabled={isDisabled}
        aria-label={ariaLabel}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        data-testid={dataTestId}
        onClick={onClickGuard}
      >
        {left}
        {labelNode}
        {!loading && rightIcon}
      </button>
    );
  },
);

export default Button;
